/**
 * ADS context (growth kit) — the only place where the app composes
 * proveedor + capping + recompensas.
 *
 * Decision chain, from outside to inside:
 *   1. `resolveAdsConfig(env, overrides)` — flags **OFF by default**
 *      (`VITE_ADS_ENABLED`), with back-office overrides on top.
 *   2. `shouldShowAds(config, tier)` from the monetization kit — **premium ⇒ 0
 *      ads immediately** (optimistic local flag: no waiting for sync).
 *   3. `makeAdProvider(...)` — with the above set to `false`, returns the no-op and
 *      **does not load any SDK** (zero network requests).
 *   4. `adGate(...)` — decides whether it is TIME to show now (capping + breakpoint).
 *
 * Guest-first: capping and rewards live in the device's `localStorage`,
 * so they work **without an account and offline**.
 */
import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  type ParentComponent,
  useContext,
} from "solid-js";
import {
  AdCapsStore,
  adGate,
  type AdGateDecision,
  type AdKind,
  type AdProvider,
  type AdReward,
  AdRewardsStore,
  adsAreLive,
  type AdsConfig,
  billingQueryKeys,
  BSO_AD_PLACEMENTS,
  bsoAdCapPolicy,
  type BsoPlacementId,
  type components,
  isReadingOrStudyRoute,
  makeBsoAdProvider,
  NoneAdProvider,
  placementDef,
  resolveAdsConfig,
  resolveMonetizationConfig,
  shouldShowAds,
} from "@bso/shared";
import { api, createApiQuery } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { adsEnv } from "./ads-env";

type ErrorModel = components["schemas"]["ErrorModel"];
type BillingConfig = components["schemas"]["BillingConfigResponse"];
type BillingUsage = components["schemas"]["BillingUsageResponse"];

/** Session signals that feed the gate (the app-shell itself updates them). */
interface SessionSignals {
  activeMs: number;
  unitsCompleted: number;
  isFirstEver: boolean;
}

interface AdsContextValue {
  config: Accessor<AdsConfig>;
  /** Are ads live? (flags + tier + network). `false` ⇒ nothing is mounted. */
  live: Accessor<boolean>;
  provider: Accessor<AdProvider>;
  /** Should this placement be shown NOW? */
  gate: (placementId: BsoPlacementId, inFlight?: boolean) => AdGateDecision;
  /** Marks a value unit as completed (list done, daily plan…). */
  completeValueUnit: () => void;
  /** Declares that a task is in progress (active input, timer, pending tx). */
  setInFlight: (value: boolean) => void;
  /** Executes an interstitial. Returns `true` if it was shown. */
  showInterstitial: (placementId: BsoPlacementId) => Promise<boolean>;
  /** Runs a rewarded ad. Returns the GRANTED reward, or `null`. */
  showRewarded: (placementId: BsoPlacementId) => Promise<AdReward | null>;
  /** Records that a rewarded offer was SHOWN (for backoff). */
  recordOffer: (placementId: BsoPlacementId) => void;
  /** Screen counters (native/banner) — do not persist. */
  setScreenCounts: (counts: { natives?: number; banners?: number }) => void;
  rewards: AdRewardsStore;
}

const AdsContext = createContext<AdsContextValue>();

const FIRST_SESSION_KEY = "ads.session.seen";

/** Safe storage: in SSR/tests without a DOM, falls back to an in-memory map. */
function safeStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function memoryStorage() {
  const map = new Map<string, string>();
  return {
    getItem: (k: string) => map.get(k) ?? null,
    setItem: (k: string, v: string) => {
      map.set(k, v);
    },
    removeItem: (k: string) => {
      map.delete(k);
    },
  };
}

export const AdsProvider: ParentComponent = (props) => {
  const session = useSession();
  const storage = safeStorage() ?? memoryStorage();

  // This provider lives in the ENTIRE app, so the query is deliberately
  // cheap: long cache and ZERO retries. If there is no response (e.g. a
  // guest, for whom the endpoint requires session) the layer falls back to env
  // config, which defaults to OFF — never retrying in a loop against the
  // server rate-limiter for an ad.
  // CAREFUL (measured bug, P23): there was also a `retry: false` here. `staleTime` is
  // per OBSERVER, but `retry` belongs to the QUERY: by sharing the cache key
  // (`billingQueryKeys.config()`) with `PlanOverview` and the profile
  // landing page, this override reconfigured the other consumers at a DISTANCE and
  // left `/profile/plan` with ZERO retries → a transient 500 rendered the entire
  // error box instead of retrying (measured: 1 request and an error after
  // ~800 ms). What makes this query cheap is the 5-minute `staleTime`, not
  // removing retries: the QueryClient default is kept (retry: 2) so
  // that ALL consumers of the key behave the same.
  const configQuery = createApiQuery<BillingConfig, ErrorModel>(
    () => billingQueryKeys.config(),
    async () => api.GET("/api/billing/config", {}),
    () => ({ staleTime: 300_000 }),
  );

  const usageQuery = createApiQuery<BillingUsage, ErrorModel>(
    () => billingQueryKeys.usage(),
    async () => api.GET("/api/billing/usage", {}),
    () => ({
      staleTime: 30_000,
      enabled: !session.isVisitor() && configQuery.data?.enabled === true,
    }),
  );

  // Bundle env + overrides served by the back-office (env → override).
  const adsConfig = createMemo(() =>
    resolveAdsConfig(
      adsEnv(),
      Object.entries(configQuery.data?.ads ?? {}).map(([key, value]) => ({ key, value })),
    ),
  );

  /**
   * Verdict from the monetization kit. The guest inherits `defaultTier`
   * (free ⇒ ads on), and premium immediately disables ads.
   */
  const monetizationSaysAds = createMemo(() => {
    const remote = configQuery.data;
    if (!remote) {
      // Guest-first: a guest CANNOT be premium (there is no subscription without
      // an account), so it inherits the default tier — free, ad-supported. For
      // a signed-in user, however, we wait for the real verdict: showing an ad
      // to someone who pays is the worst of both worlds.
      return session.isVisitor();
    }
    const config = resolveMonetizationConfig({}, []);
    config.enabled = remote.enabled;
    config.defaultTier = remote.defaultTier;
    config.tiers = remote.tiers.map((t) => ({
      id: t.id,
      label: t.label,
      adsEnabled: t.adsEnabled,
      limits: {
        storage_bytes: t.limits.storageBytes ?? null,
        items: t.limits.maxItems ?? null,
        collections: t.limits.maxCollections ?? null,
        orgs: t.limits.maxOrgs ?? null,
        attachments: t.limits.maxAttachments ?? null,
      },
    }));
    return shouldShowAds(config, usageQuery.data?.tier);
  });

  const live = createMemo(() => adsAreLive(adsConfig(), monetizationSaysAds()));

  // ALWAYS starts with the no-op: while it is unconfirmed that ads are live,
  // there is not even the possibility of a request.
  const [provider, setProvider] = createSignal<AdProvider>(new NoneAdProvider());

  const caps = new AdCapsStore(storage, session.userId() || "guest");
  const rewards = new AdRewardsStore(storage, session.userId() || "guest");

  const startedAt = Date.now();
  const [unitsCompleted, setUnitsCompleted] = createSignal(0);
  const [inFlight, setInFlight] = createSignal(false);

  // Reading/study = task in progress, always. By route PREFIX, so
  // a future study screen is covered without having to remember anything.
  const readingOrStudy = () => isReadingOrStudyRoute(globalThis.location?.pathname ?? "/");

  const isFirstEver = (() => {
    const seen = storage.getItem(FIRST_SESSION_KEY) !== null;
    storage.setItem(FIRST_SESSION_KEY, "1");
    return !seen;
  })();

  const sessionSignals = (): SessionSignals => ({
    activeMs: Date.now() - startedAt,
    unitsCompleted: unitsCompleted(),
    isFirstEver,
  });

  // The SDK loads only when ads are LIVE: while `live()` is
  // false there is no real provider, so no request is possible.
  createEffect(() => {
    if (!live()) {
      setProvider(() => new NoneAdProvider());
      // After a premium purchase: 0 ads immediately ⇒ local capping is forgotten.
      caps.reset();
      return;
    }
    const next = makeBsoAdProvider(adsConfig());
    const config = adsConfig();
    void (async () => {
      await next.init({
        childDirected: config.childDirected,
        maxAdContentRating: config.maxAdContentRating,
      });
      setProvider(() => next);
    })();
  });

  const gate = (placementId: BsoPlacementId, taskInFlight?: boolean): AdGateDecision => {
    const def = placementDef(BSO_AD_PLACEMENTS, placementId);
    if (!def || !live()) {
      // An undeclared placement does not exist: nothing is rendered.
      return { allow: false, reason: "premium" };
    }
    if (readingOrStudy()) {
      return { allow: false, reason: "in-flight" };
    }
    return adGate({
      kind: def.kind,
      placementId,
      breakpoint: def.breakpoint,
      rewardType: def.reward?.type,
      now: Date.now(),
      caps: caps.snapshot(),
      session: sessionSignals(),
      user: { premium: !monetizationSaysAds(), child: adsConfig().childDirected },
      // ⚠️ bso rule #1, enforced at RUNTIME and not only at registration:
      // if the user is in the reader or on a study screen, the
      // decision is "no" before checking any counter. The registration guard
      // (`assertBsoAdPolicy`) prevents DECLARING a placement there; this prevents a
      // legitimate one from firing while the user is reading — e.g. if a
      // component stayed mounted after navigating to the chapter.
      inFlight: taskInFlight ?? inFlight() ?? false,
      // bso capping only TIGHTENS the portfolio cap (§4), and it is built
      // through the ONLY existing gate: `bsoAdCapPolicy` merges taking the
      // strictest option. A spread here (`{ ...BSO_AD_CAP_POLICY, ...config }`) would allow
      // the configuration to LOOSEN the regime before the gate could
      // defend it — exactly the hole this closes.
      policy: bsoAdCapPolicy(),
    });
  };

  const showKind = async (
    placementId: BsoPlacementId,
    kind: AdKind,
  ): Promise<{ shown: boolean; reward?: AdReward }> => {
    if (!gate(placementId).allow) {
      return { shown: false };
    }
    const p = provider();
    if (!(await p.prepare(kind, placementId))) {
      // No fill: the UI collapses the gap. Never a spinner waiting for an ad.
      return { shown: false };
    }
    return p.show(kind, placementId);
  };

  const value: AdsContextValue = {
    config: adsConfig,
    live,
    provider,
    gate,
    completeValueUnit: () => setUnitsCompleted((n) => n + 1),
    setInFlight,
    rewards,
    recordOffer: (placementId) => caps.recordOffer(placementId),
    setScreenCounts: (counts) => caps.setScreenCounts(counts),
    showInterstitial: async (placementId) => {
      const result = await showKind(placementId, "interstitial");
      if (result.shown) {
        caps.recordInterstitial();
      }
      return result.shown;
    },
    showRewarded: async (placementId) => {
      const def = placementDef(BSO_AD_PLACEMENTS, placementId);
      const result = await showKind(placementId, "rewarded");
      // The provider's `reward` only CERTIFIES that the user completed the
      // ad; watching it halfway does not earn a reward. WHAT is awarded ALWAYS comes from
      // the placement registry, never from the network payload (which is external
      // input). If the reward touches the backend, the server verifies it again.
      const granted = result.reward ? rewards.grant(def?.reward) : null;
      if (granted) {
        caps.recordReward({ type: granted.type, amount: granted.amount }, placementId);
        return { type: granted.type, amount: granted.amount };
      }
      return null;
    },
  };

  return <AdsContext.Provider value={value}>{props.children}</AdsContext.Provider>;
};

/**
 * Access to the ads context. Returns `undefined` if no provider is mounted
 * — intentionally: an app without ads (tiers C/D) must not fail for not mounting it.
 */
export function useAds(): AdsContextValue | undefined {
  return useContext(AdsContext);
}
